import { Fragment, useEffect, useRef, useState } from "react";
import { formatFileSize } from "./helpers";
import { brand } from "../../../context";
import { BackButton } from "./componets";
import { createQuotationUploadMode } from "../../../api/quotation";
import { toast } from "react-toastify";
import { QuotationRequestDocumentParsingLoader } from "./QuotationRequestLoader";

type UploadFileFormProps = {
  file: File | null;
  onFileChange: (file: File | null) => void;
};

function UploadFileForm({ file, onFileChange }: UploadFileFormProps) {
  useEffect(() => {
    onFileChange(null);
  }, [onFileChange]);

  return (
    <div className="grid gap-1">
      <label className="font-medium">
        Upload File {!!file ? `(Size: ${formatFileSize(file.size)})` : ""}
      </label>
      <input
        className="border border-gray-300 rounded-md p-1 cursor-pointer"
        type="file"
        name="file"
        // accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg,image/jpg"
        accept="application/pdf,image/png,image/jpeg,image/jpg"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFileChange(file);
        }}
      />
    </div>
  );
}

async function SSEClient(file: File, setText: (text: string) => void, setSSEDone: () => void) {
  const response = await createQuotationUploadMode({ file });
  const reader = response.body?.getReader();
  if (!reader) {
    setSSEDone();
    return;
  }

  const decoder = new TextDecoder("utf-8");

  let parsingError = false;
  while (true) {
    const { value, done } = await reader.read();
    if (done || parsingError) {
      setSSEDone();
      break;
    }

    const text = decoder.decode(value, { stream: true });
    const textArray = text.split("\n");
    for (const line of textArray) {
      if (line.trim() === "") continue;
      if (!line.startsWith("data:")) continue;

      try {
        const jsonData = window.atob(line.replace("data:", ""));
        setText(jsonData);
      } catch {
        parsingError = true;
        break;
      }
    }
  }

  //   const text = decoder.decode(value);
  //   let validText = text.match(/\{([^}]*)\}/)?.[1] || "";
  //   if (!validText) {
  //     continue;
  //   }

  //   validText = `{${validText}}`;

  //   try {
  //     const json = JSON.parse(validText);
  //     setText(json.content);
  //   } catch {
  //     parsingError = true;
  //     break;
  //   }
  // }
}

type UploadQuotationRequestDocProps = {
  file: File | null;
  onSetParsedFile: (parsedFile: string) => void;
  onFileChange: (file: File | null) => void;
  onBack: () => void;
};
export function UploadQuotationRequestDoc({
  file,
  onFileChange,
  onSetParsedFile,
  onBack,
}: UploadQuotationRequestDocProps) {
  const [sseDone, setSSEDone] = useState(false);
  const [text, setText] = useState<string | null>(null);

  // Move into the next step when the SSE is done and the text is not empty
  useEffect(() => {
    if (sseDone && !!text) {
      onSetParsedFile(text);
      return;
    }

    if (sseDone && !text) {
      toast.error("Failed to parse the document. Please try again.");
      onBack();
    }
  }, [sseDone, text, onSetParsedFile, onBack]);

  // Auto scroll to the bottom of the parsed file when the text is updated
  useEffect(() => {
    if (parsedFileContainer.current && !!text) {
      parsedFileContainer.current.scrollTo({
        top: parsedFileContainer.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [text]);

  const parsedFileContainer = useRef<HTMLDivElement>(null);

  return text !== null && text === "" ? (
    <QuotationRequestDocumentParsingLoader />
  ) : !!text ? (
    <div className="Modal__card__body overflow-hidden grid grid-rows-[auto_1fr]">
      <h6>
        Preview Document <br />
        <span className="text-sm text-gray-500">
          This might take a while, please wait for the document to be parsed.
        </span>
      </h6>
      <div
        ref={parsedFileContainer}
        className="grid border border-gray-200 rounded-md p-3 overflow-auto"
      >
        <p className="whitespace-pre-wrap">{text}</p>
      </div>
    </div>
  ) : (
    <Fragment>
      <div className="Modal__card__body grid auto-rows-max">
        <h6>Upload Quotation Request Document</h6>
        <span className="text-sm text-gray-500 border border-gray-200 rounded-md p-3">
          Please upload the quotation request document that you want to send to us. The document
          will be parsed and the information will be used to create a quotation request. The
          document should be in PDF, Word, PNG, JPG, or JPEG format. Please don't upload documents
          larger than 500KB when uploading documents and larger than 2MB when uploading images.
        </span>
        <UploadFileForm file={file} onFileChange={onFileChange} />
      </div>
      <div className="Modal__card__actions">
        <BackButton onBack={onBack} />
        <button
          className="Button"
          type="button"
          data-brand={brand}
          onClick={async (e) => {
            e.preventDefault();
            if (!file) {
              toast.warning("Please choose a file first.");
              return;
            }

            setText("");
            await SSEClient(file, setText, () => {
              setSSEDone(true);
            });
          }}
        >
          Submit Document
        </button>
      </div>
    </Fragment>
  );
}
